import React, { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import { useTranslation } from "react-i18next";

const AddressModal = ({
  showModal,
  setShowModal,
  formData,
  handleChange,
  handleFormSubmit,
  resetFormData,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const bsModalRef = useRef(null);

  const [errors, setErrors] = useState({});

  // -------------------------------
  // LIVE VALIDATION ON TYPING
  // -------------------------------
  const validateField = (name, value) => {
    let msg = "";

    switch (name) {
      case "name":
        if (!value.trim() || value.length < 2) msg = "Enter a valid name (min 2 characters)";
        break;

      case "street":
        if (!value.trim()) msg = "Street is required";
        break;

      case "house_no":
        if (!value.trim()) msg = "House No. is required";
        break;

      case "pincode":
        if (!/^[0-9]{6}$/.test(value)) msg = "Enter a valid 6-digit pincode";
        break;

      case "city":
        if (!value.trim() || /\d/.test(value)) msg = "Enter a valid city name";
        break;

      case "state":
        if (!value.trim() || /\d/.test(value)) msg = "Enter a valid state name";
        break;

      case "country":
        if (!value.trim() || /\d/.test(value)) msg = "Enter a valid country name";
        break;

      case "mobile":
        if (!/^[6-9]\d{9}$/.test(value)) msg = "Enter a valid 10-digit mobile number";
        break;

      case "email":
        if (!/^\S+@\S+\.\S+$/.test(value)) msg = "Enter a valid email address";
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  // Handle change + validation
  const handleInputChange = (e) => {
    handleChange(e); // original parent handler
    validateField(e.target.name, e.target.value);
  };

  // Final Submit validation
  const validateFormBeforeSubmit = (e) => {
    e.preventDefault();

    const fields = [
      "name",
      "street",
      "house_no",
      "pincode",
      "city",
      "state",
      "country",
      "mobile",
      "email",
    ];

    const newErrors = {};

    fields.forEach((key) => {
      validateField(key, formData[key]);
      if (!formData[key] || errors[key]) {
        newErrors[key] = errors[key] || "This field is required";
      }
    });

    // Stop submit if errors found
    if (Object.values(newErrors).some((x) => x)) {
      setErrors(newErrors);
      return;
    }

    // No errors → submit
    setErrors({});
    handleFormSubmit(e);
  };

  // Modal open/close
  useEffect(() => {
    if (modalRef.current && !bsModalRef.current) {
      bsModalRef.current = new Modal(modalRef.current, {
        backdrop: true,
        keyboard: true,
      });

      modalRef.current.addEventListener("hidden.bs.modal", () => {
        setShowModal(false);
        if (resetFormData) resetFormData();
        setErrors({});
      });
    }

    const modalInstance = bsModalRef.current;
    if (showModal) modalInstance.show();
    else modalInstance.hide();
  }, [showModal]);

  const closeModal = () => {
    setShowModal(false);
    if (resetFormData) resetFormData();
    setErrors({});
  };

  return (
    <>
      <div
        ref={modalRef}
        className="modal fade"
        id="addressModal"
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {formData.id ? t("Edit_Address") : t("Add_New_Address")}
              </h5>
              <button type="button" className="btn-close" onClick={closeModal} />
            </div>

            <div className="modal-body">
              <form id="add-address-form" onSubmit={validateFormBeforeSubmit}>
                <div className="row">

                  {/* Name */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">
                      {t("Name")} *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t("Full_Name")}
                    />
                    {errors.name && <small className="text-danger">{errors.name}</small>}
                  </div>

                  {/* Street */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("Street")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.street ? "is-invalid" : ""}`}
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      placeholder={t("Street")}
                    />
                    {errors.street && <small className="text-danger">{errors.street}</small>}
                  </div>

                  {/* House No */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("House_No.")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.house_no ? "is-invalid" : ""}`}
                      name="house_no"
                      value={formData.house_no}
                      onChange={handleInputChange}
                      placeholder={t("House_No.")}
                    />
                    {errors.house_no && <small className="text-danger">{errors.house_no}</small>}
                  </div>

                  {/* Pincode */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("Zip_Code")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.pincode ? "is-invalid" : ""}`}
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      placeholder={t("Zip_Code")}
                    />
                    {errors.pincode && <small className="text-danger">{errors.pincode}</small>}
                  </div>

                  {/* City */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("City")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.city ? "is-invalid" : ""}`}
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder={t("City")}
                    />
                    {errors.city && <small className="text-danger">{errors.city}</small>}
                  </div>

                  {/* State */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("State")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.state ? "is-invalid" : ""}`}
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder={t("State")}
                    />
                    {errors.state && <small className="text-danger">{errors.state}</small>}
                  </div>

                  {/* Country */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("Country")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.country ? "is-invalid" : ""}`}
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder={t("Country")}
                    />
                    {errors.country && <small className="text-danger">{errors.country}</small>}
                  </div>

                  {/* Mobile */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("Mobile_Number")} *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder={t("Mobile_Number")}
                    />
                    {errors.mobile && <small className="text-danger">{errors.mobile}</small>}
                  </div>

                  {/* Email */}
                  <div className="mb-3 col-md-6">
                    <label className="form-label">{t("Email")} *</label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t("Email")}
                    />
                    {errors.email && <small className="text-danger">{errors.email}</small>}
                  </div>

                  {/* Default checkbox */}
                  <div className="col-md-12 d-flex align-items-center mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="is_default"
                        name="is_default"
                        checked={formData.is_default === 1}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label fw-semibold ms-2">
                        {t("Set_as_Default")}
                      </label>
                    </div>
                  </div>

                </div>

                <button type="submit" className="btn btn-primary">
                  {t("Save_Address")}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default AddressModal;
